import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Rating,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  Paper,
  Stack,
  Skeleton,
  Modal,
  Badge,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import PhotoIcon from '@mui/icons-material/Photo';
import CloseIcon from '@mui/icons-material/Close';
import { RootState, AppDispatch } from '../../app/store';
import {
  fetchPlaces,
  fetchPlaceWithReviews,
  fetchPlaceAnalytics,
  clearSelectedPlace,
} from '../../features/googleReviews/googleReviewsSlice';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import api from '../../config/api';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Brand {
  brand_id: string;
  brand_name: string;
}

const GoogleReviewsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    places,
    selectedPlace,
    placeReviews,
    placeAnalytics,
    loading,
    error,
  } = useSelector((state: RootState) => state.googleReviews);
  const token = useSelector((state: RootState) => state.auth.token);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedStars, setSelectedStars] = useState<number[]>([1, 2, 3, 4, 5]);
  const [placeStarFilter, setPlaceStarFilter] = useState<number[]>([1, 2, 3, 4, 5]);
  const [sortBy, setSortBy] = useState<'name-asc' | 'name-desc' | 'rating-asc' | 'rating-desc'>('rating-desc');
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');

  useEffect(() => {
    // Fetch brands
    const fetchBrands = async () => {
      try {
        const response = await api.get('/feedback/brands');
        const googleBrands = response.data.data.filter((b: any) => b.platform === 'google');
        setBrands(googleBrands);
      } catch (error) {
        console.error('Error fetching brands:', error);
      }
    };
    void fetchBrands();
  }, []);

  useEffect(() => {
    void dispatch(fetchPlaces(selectedBrand));
  }, [dispatch, selectedBrand]);

  // Redirect if not authenticated
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const handlePlaceSelect = async (placeId: string) => {
    dispatch(clearSelectedPlace());
    await Promise.all([
      dispatch(fetchPlaceWithReviews(placeId)),
      dispatch(fetchPlaceAnalytics(placeId))
    ]);
  };

  const handleOpenImage = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setOpenImageModal(true);
  };

  const handleCloseImageModal = () => {
    setOpenImageModal(false);
  };

  const filteredPlaces = places.filter((place) =>
    place.title.toLowerCase().includes(searchTerm.toLowerCase()) && 
    placeStarFilter.some(star => {
      // Round the total_score to the nearest integer for filtering
      const roundedRating = Math.round(place.total_score);
      return star === roundedRating;
    })
  );

  const filteredReviews = placeReviews.filter((review) => 
    selectedStars.includes(review.stars)
  );

  const sortedPlaces = [...filteredPlaces].sort((a, b) => {
    if (sortBy === 'name-asc') {
      return a.title.localeCompare(b.title);
    } else if (sortBy === 'name-desc') {
      return b.title.localeCompare(a.title);
    } else if (sortBy === 'rating-asc') {
      return a.total_score - b.total_score;
    } else if (sortBy === 'rating-desc') {
      return b.total_score - a.total_score;
    }
    return 0;
  });

  if (loading && !selectedPlace) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const starCounts = ['five', 'four', 'three', 'two', 'one'] as const;
  type StarCount = typeof starCounts[number];

  return (
    <Container maxWidth="xl">
      <Box my={4}>
        <Typography variant="h4" gutterBottom fontWeight="600">
          Google Reviews Analytics
        </Typography>

        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search Places"
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ bgcolor: 'background.paper' }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                <InputLabel>Filter by Brand</InputLabel>
                <Select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  label="Filter by Brand"
                >
                  <MenuItem value="">All Brands</MenuItem>
                  {brands.map((brand) => (
                    <MenuItem key={brand.brand_id} value={brand.brand_id}>
                      {brand.brand_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                <InputLabel id="place-star-filter-label">Filter Places by Rating</InputLabel>
                <Select
                  labelId="place-star-filter-label"
                  multiple
                  value={placeStarFilter}
                  onChange={(e) => setPlaceStarFilter(typeof e.target.value === 'string' 
                    ? JSON.parse(e.target.value) 
                    : e.target.value)}
                  renderValue={(selected) => 
                    `Showing ${selected.length === 5 ? 'All' : selected.join(', ')} Star Places`
                  }
                  label="Filter Places by Rating"
                >
                  {[5, 4, 3, 2, 1].map((star) => (
                    <MenuItem key={star} value={star}>
                      <Checkbox checked={placeStarFilter.includes(star)} />
                      <ListItemText 
                        primary={
                          <Box display="flex" alignItems="center">
                            <Rating value={star} readOnly size="small" sx={{ mr: 1 }} />
                            <Typography variant="body2">{`${star} Star`}</Typography>
                          </Box>
                        } 
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                <InputLabel id="sort-by-label">Sort By</InputLabel>
                <Select
                  labelId="sort-by-label"
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value as any)}
                  label="Sort By"
                >
                  <MenuItem value="name-asc">Name (A-Z)</MenuItem>
                  <MenuItem value="name-desc">Name (Z-A)</MenuItem>
                  <MenuItem value="rating-desc">Rating (High-Low)</MenuItem>
                  <MenuItem value="rating-asc">Rating (Low-High)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Places ({sortedPlaces.length})
              </Typography>
            </Paper>
            
            {sortedPlaces.map((place, index) => (
              <Paper
                key={place.id}
                elevation={selectedPlace?.id === place.id ? 4 : 1}
                sx={{
                  mb: 2,
                  cursor: 'pointer',
                  p: 0,
                  position: 'relative',
                  bgcolor: selectedPlace?.id === place.id ? 'primary.light' : 'background.paper',
                  '&:hover': {
                    bgcolor: selectedPlace?.id === place.id ? 'primary.light' : 'action.hover',
                  },
                  transition: 'all 0.2s ease-in-out',
                  transform: selectedPlace?.id === place.id ? 'scale(1.01)' : 'scale(1)',
                  borderRadius: 2,
                }}
                onClick={() => void handlePlaceSelect(place.id)}
              >
                {sortBy === 'rating-desc' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      bgcolor: 'primary.main',
                      color: 'white',
                      width: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                    }}
                  >
                    {index + 1}
                  </Box>
                )}
                <CardContent>
                  <Typography variant="h6" fontWeight="500">{place.title}</Typography>
                  <Typography color="textSecondary" gutterBottom fontSize="0.875rem">
                    {place.address}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Rating value={place.total_score} precision={0.1} readOnly />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {typeof place.total_score === 'number' && !isNaN(place.total_score)
                        ? place.total_score.toFixed(1)
                        : Number(place.total_score) && !isNaN(Number(place.total_score))
                          ? Number(place.total_score).toFixed(1)
                          : '-'}
                    </Typography>
                    <Typography>({place.reviews_count} reviews)</Typography>
                  </Box>
                </CardContent>
              </Paper>
            ))}
          </Grid>

          <Grid item xs={12} md={8}>
            {selectedPlace && placeAnalytics ? (
              <>
                <Paper elevation={3} sx={{ mb: 4, p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom fontWeight="600">
                    Analytics Overview
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default' }}>
                        <Typography variant="subtitle2" color="text.secondary">Total Reviews</Typography>
                        <Typography variant="h4" fontWeight="600" color="primary.main">
                          {placeAnalytics.overall_stats.total_reviews}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default' }}>
                        <Typography variant="subtitle2" color="text.secondary">Average Rating</Typography>
                        <Box display="flex" alignItems="center">
                          <Typography variant="h4" sx={{ mr: 1 }} fontWeight="600" color="primary.main">
                            {placeAnalytics.overall_stats.average_rating.toFixed(1)}
                          </Typography>
                          <Rating
                            value={placeAnalytics.overall_stats.average_rating}
                            precision={0.1}
                            readOnly
                          />
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default' }}>
                        <Typography variant="subtitle2" color="text.secondary">Rating Distribution</Typography>
                        <Box>
                          {[5, 4, 3, 2, 1].map((stars, index) => (
                            <Box key={stars} display="flex" alignItems="center" gap={1}>
                              <Rating value={stars} readOnly size="small" />
                              <Typography variant="body2">
                                {placeAnalytics.overall_stats[`${starCounts[index]}_star_count`]}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>

                <Paper elevation={3} sx={{ mb: 4, p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom fontWeight="600">
                    Review Trend
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={placeAnalytics.review_trend}>
                      <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.5} />
                      <XAxis
                        dataKey="month"
                        tickFormatter={(value: string) => dayjs(value).format('MMM YYYY')}
                      />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip
                        labelFormatter={(value: string) => dayjs(value).format('MMMM YYYY')}
                        contentStyle={{ borderRadius: 8, boxShadow: '0 4px 8px rgba(0,0,0,0.1)', border: 'none' }}
                      />
                      <Legend />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="review_count"
                        name="Review Count"
                        stroke="#8884d8"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="average_rating"
                        name="Average Rating"
                        stroke="#82ca9d"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>

                <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom fontWeight="600">
                    Recent Reviews
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel id="star-filter-label">Filter by Stars</InputLabel>
                      <Select
                        labelId="star-filter-label"
                        multiple
                        value={selectedStars}
                        onChange={(e) => setSelectedStars(typeof e.target.value === 'string' 
                          ? JSON.parse(e.target.value) 
                          : e.target.value)}
                        renderValue={(selected) => 
                          `Showing ${selected.length === 5 ? 'All' : selected.join(', ')} Star Reviews`
                        }
                        label="Filter by Stars"
                      >
                        {[5, 4, 3, 2, 1].map((star) => (
                          <MenuItem key={star} value={star}>
                            <Checkbox checked={selectedStars.includes(star)} />
                            <ListItemText 
                              primary={
                                <Box display="flex" alignItems="center">
                                  <Rating value={star} readOnly size="small" sx={{ mr: 1 }} />
                                  <Typography variant="body2">{`${star} Star`}</Typography>
                                </Box>
                              } 
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Paper>
                
                {filteredReviews.map((review) => (
                  <Paper key={review.id} elevation={2} sx={{ mb: 2, p: 0, borderRadius: 2, overflow: 'hidden' }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {review.reviewer_name[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="500">{review.reviewer_name}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {dayjs(review.published_at).format('MMMM D, YYYY')}
                          </Typography>
                        </Box>
                      </Box>
                      <Rating value={review.stars} readOnly sx={{ mb: 1 }} />
                      <Typography variant="body1" paragraph>
                        {review.text}
                      </Typography>
                      {review.detailed_ratings && (
                        <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                          {review.detailed_ratings.map((rating) => (
                            <Chip
                              key={rating.category}
                              label={`${rating.category}: ${rating.rating}/5`}
                              size="small"
                              sx={{ borderRadius: 1 }}
                            />
                          ))}
                        </Box>
                      )}
                      {review.review_images && review.review_images.length > 0 && review.review_images[0] !== null && (
                        <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
                          <Button 
                            variant="outlined" 
                            startIcon={<PhotoIcon />}
                            onClick={() => review.review_images && handleOpenImage(review.review_images[0])}
                            size="small"
                          >
                            View {review.review_images.length} {review.review_images.length === 1 ? 'Image' : 'Images'}
                          </Button>
                        </Box>
                      )}
                      {review.owner_response_text && (
                        <Box mt={2} p={2} borderRadius={1} bgcolor="background.default">
                          <Typography variant="body2" fontWeight="500" color="text.secondary">
                            Owner Response:
                          </Typography>
                          <Typography variant="body2">{review.owner_response_text}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(review.owner_response_date).format('MMMM D, YYYY')}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Paper>
                ))}
              </>
            ) : (
              <Paper elevation={3} sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', borderRadius: 2 }}>
                <Typography color="textSecondary" align="center">
                  Select a place to view its analytics and reviews
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Box>
      
      {/* Image Modal */}
      <Modal
        open={openImageModal}
        onClose={handleCloseImageModal}
        aria-labelledby="image-modal"
        aria-describedby="review-image-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxWidth: '90vw',
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 1,
        }}>
          <IconButton
            onClick={handleCloseImageModal}
            sx={{ position: 'absolute', right: 8, top: 8, bgcolor: 'rgba(255,255,255,0.8)', zIndex: 10 }}
          >
            <CloseIcon />
          </IconButton>
          <img 
            src={selectedImage} 
            alt="Review" 
            style={{ maxWidth: '100%', maxHeight: 'calc(90vh - 32px)', objectFit: 'contain' }} 
          />
        </Box>
      </Modal>
    </Container>
  );
};

export default GoogleReviewsPage; 